import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { useImportsApi } from '../../services/imports';
import type { ImportSheetName, ImportSheetResult, ImportWorkbookResult } from '../../types/imports';
import { IMPORT_SHEET_LABELS } from '../../types/imports';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

type DashboardImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => Promise<void> | void;
};

const getDownloadFileName = () => 'plantilla-importacion-interiorlab.xlsx';

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

const getInitialTab = (result: ImportWorkbookResult | null): ImportSheetName => {
  const firstPresentSheet = result?.sheets.find((sheet) => sheet.present)?.sheet_name;
  return firstPresentSheet ?? 'proyectos';
};

const getSheetBadge = (sheet: ImportSheetResult) => {
  if (!sheet.present) return { label: 'Omitida', variant: 'outline' as const };
  if (sheet.rejected_rows > 0) return { label: 'Con errores', variant: 'destructive' as const };
  if (sheet.valid_rows > 0 || sheet.imported_rows > 0) {
    return { label: 'Correcta', variant: 'finished' as const };
  }
  return { label: 'Sin filas', variant: 'secondary' as const };
};

export function DashboardImportDialog({
  open,
  onOpenChange,
  onCompleted,
}: DashboardImportDialogProps) {
  const { downloadTemplate, validateWorkbook, executeWorkbook } = useImportsApi();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ImportWorkbookResult | null>(null);
  const [executionResult, setExecutionResult] = useState<ImportWorkbookResult | null>(null);
  const [activeTab, setActiveTab] = useState<ImportSheetName>('proyectos');
  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);

  const visibleResult = executionResult ?? validationResult;
  const sortedSheets = useMemo(() => visibleResult?.sheets ?? [], [visibleResult]);
  const canExecute =
    Boolean(selectedFile) &&
    Boolean(validationResult) &&
    (validationResult?.summary.valid_rows ?? 0) > 0 &&
    !executionResult &&
    !executing &&
    !validating;
  const dialogWidthClassName = visibleResult
    ? 'w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] sm:max-w-5xl xl:max-w-6xl 2xl:max-w-[1400px]'
    : 'w-[calc(100vw-2rem)] sm:w-[calc(100vw-4rem)] sm:max-w-3xl xl:max-w-4xl';

  useEffect(() => {
    if (!open) return;
    setActiveTab(getInitialTab(visibleResult));
  }, [open, visibleResult]);

  useEffect(() => {
    if (!open) return;
    if (!selectedFile) {
      setValidationResult(null);
      setExecutionResult(null);
    }
  }, [open, selectedFile]);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const blob = await downloadTemplate();
      triggerBlobDownload(blob, getDownloadFileName());
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No pudimos descargar la plantilla.');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file);
    setValidationResult(null);
    setExecutionResult(null);
    setActiveTab('proyectos');
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo Excel antes de validar.');
      return;
    }

    try {
      setValidating(true);
      const result = await validateWorkbook(selectedFile);
      setValidationResult(result);
      setExecutionResult(null);
      setActiveTab(getInitialTab(result));
      if (result.summary.rejected_rows > 0) {
        toast.error('La validación terminó con filas rechazadas. Revisa el detalle por pestaña.');
      } else {
        toast.success('Archivo validado correctamente.');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No pudimos validar el archivo.');
    } finally {
      setValidating(false);
    }
  };

  const handleExecute = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo Excel antes de importar.');
      return;
    }
    if (!validationResult || validationResult.summary.valid_rows === 0) {
      toast.error('Valida primero el archivo antes de confirmar la importación.');
      return;
    }

    try {
      setExecuting(true);
      const result = await executeWorkbook(selectedFile);
      setExecutionResult(result);
      setActiveTab(getInitialTab(result));
      if (result.summary.imported_rows > 0) {
        toast.success('Importación completada.');
      }
      if (onCompleted) {
        await onCompleted();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'No pudimos completar la importación.');
    } finally {
      setExecuting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && (validating || executing || downloading)) return;
    if (!nextOpen) {
      setSelectedFile(null);
      setValidationResult(null);
      setExecutionResult(null);
      setActiveTab('proyectos');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-h-[88vh] overflow-y-auto p-0 ${dialogWidthClassName}`}>
        <div className="flex min-h-0 flex-col">
          <DialogHeader className="border-b px-6 py-5">
            <DialogTitle>Importar datos desde Excel</DialogTitle>
            <DialogDescription>
              Descarga la plantilla, sube el archivo y valida el contenido antes de confirmar la importación.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-5 overflow-x-hidden px-6 py-5">
            <div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-700">Archivo Excel</p>
              </div>

              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  className="sr-only"
                  disabled={validating || executing}
                  onChange={(event) => {
                    handleFileChange(event.target.files?.[0] ?? null);
                    event.currentTarget.value = '';
                  }}
                />

                <Button
                  type="button"
                  variant="outline"
                  className="min-w-[190px] justify-center truncate"
                  disabled={validating || executing}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Seleccionar archivo
                </Button>

                <p className="text-xs text-gray-500">Solo se admite formato .xlsx</p>

                {selectedFile ? (
                  <p className="break-all text-xs text-gray-600">
                    Archivo seleccionado: {selectedFile.name}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="min-w-[190px]"
                  onClick={() => void handleDownloadTemplate()}
                  disabled={downloading || validating || executing}
                >
                  {downloading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Descargar plantilla
                </Button>

                <Button
                  type="button"
                  onClick={() => void handleValidate()}
                  disabled={!selectedFile || validating || executing}
                >
                  {validating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-2 h-4 w-4" />
                  )}
                  Validar archivo
                </Button>
              </div>
            </div>

            {visibleResult ? (
              <div className="min-w-0 space-y-4 pb-2">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-gray-500">Filas leídas</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {visibleResult.summary.total_rows}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-gray-500">Filas válidas</p>
                    <p className="text-2xl font-semibold text-emerald-700">
                      {visibleResult.summary.valid_rows}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-gray-500">Filas importadas</p>
                    <p className="text-2xl font-semibold text-blue-700">
                      {visibleResult.summary.imported_rows}
                    </p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <p className="text-sm text-gray-500">Filas rechazadas</p>
                    <p className="text-2xl font-semibold text-red-700">
                      {visibleResult.summary.rejected_rows}
                    </p>
                  </div>
                </div>

                {visibleResult.summary.rejected_rows > 0 ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Hay filas con errores</AlertTitle>
                    <AlertDescription>
                      Revisa cada pestaña para ver qué filas no se van a importar y el motivo exacto.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>
                      {visibleResult.mode === 'validate'
                        ? 'Validación correcta'
                        : 'Importación completada'}
                    </AlertTitle>
                    <AlertDescription>
                      {visibleResult.mode === 'validate'
                        ? 'El archivo se puede importar con las filas válidas detectadas.'
                        : 'La importación terminó sin filas rechazadas.'}
                    </AlertDescription>
                  </Alert>
                )}

                <Tabs
                  value={activeTab}
                  onValueChange={(value) => setActiveTab(value as ImportSheetName)}
                  className="min-w-0"
                >
                  <TabsList className="grid h-auto w-full grid-cols-1 gap-2 rounded-xl p-1 sm:grid-cols-2 xl:grid-cols-5">
                    {sortedSheets.map((sheet) => {
                      const badge = getSheetBadge(sheet);
                      return (
                        <TabsTrigger
                          key={sheet.sheet_name}
                          value={sheet.sheet_name}
                          className="min-w-0 flex-col items-start gap-1 px-3 py-2 text-left whitespace-normal"
                        >
                          <span>{IMPORT_SHEET_LABELS[sheet.sheet_name]}</span>
                          <Badge variant={badge.variant}>{badge.label}</Badge>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>

                  {sortedSheets.map((sheet) => (
                    <TabsContent key={sheet.sheet_name} value={sheet.sheet_name} className="mt-4 min-w-0">
                      <div className="min-w-0 space-y-4 rounded-lg border p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-gray-500" />
                          <h3 className="text-sm font-semibold text-gray-900">
                            {IMPORT_SHEET_LABELS[sheet.sheet_name]}
                          </h3>
                          {!sheet.present ? <Badge variant="outline">Hoja omitida</Badge> : null}
                        </div>

                        {sheet.messages.length > 0 ? (
                          <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Problemas de estructura</AlertTitle>
                            <AlertDescription>
                              {sheet.messages.map((message) => (
                                <p key={message}>{message}</p>
                              ))}
                            </AlertDescription>
                          </Alert>
                        ) : null}

                        <div className="grid gap-3 sm:grid-cols-3">
                          <div className="rounded-md border p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500">Filas</p>
                            <p className="text-lg font-semibold text-gray-900">{sheet.total_rows}</p>
                          </div>
                          <div className="rounded-md border p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500">Válidas</p>
                            <p className="text-lg font-semibold text-emerald-700">{sheet.valid_rows}</p>
                          </div>
                          <div className="rounded-md border p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500">Rechazadas</p>
                            <p className="text-lg font-semibold text-red-700">{sheet.rejected_rows}</p>
                          </div>
                        </div>

                        <ScrollArea className="h-[280px] max-w-full rounded-md border lg:h-[320px]">
                          <div className="divide-y">
                            {sheet.rows.length === 0 ? (
                              <div className="p-4 text-sm text-gray-500">
                                No hay filas para mostrar en esta pestaña.
                              </div>
                            ) : (
                              sheet.rows.map((row) => (
                                <div
                                  key={`${sheet.sheet_name}-${row.row_number}-${row.identifier ?? 'sin-id'}`}
                                  className="flex items-start justify-between gap-4 p-4"
                                >
                                  <div className="min-w-0 space-y-1">
                                    <p className="text-sm font-medium text-gray-900">
                                      Fila {row.row_number}
                                      {row.identifier ? ` · ${row.identifier}` : ''}
                                    </p>
                                    {row.reason ? (
                                      <p className="break-words text-sm text-gray-600">{row.reason}</p>
                                    ) : null}
                                  </div>
                                  <Badge
                                    variant={row.status === 'rejected' ? 'destructive' : 'finished'}
                                    className="shrink-0"
                                  >
                                    {row.status === 'rejected' ? (
                                      <XCircle className="h-3 w-3" />
                                    ) : (
                                      <CheckCircle2 className="h-3 w-3" />
                                    )}
                                    {row.status === 'rejected'
                                      ? 'Rechazada'
                                      : row.status === 'imported'
                                        ? 'Importada'
                                        : 'Validada'}
                                  </Badge>
                                </div>
                              ))
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            ) : null}
          </div>

          <DialogFooter className="border-t px-6 py-4">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={validating || executing || downloading}
            >
              Cerrar
            </Button>
            <Button onClick={() => void handleExecute()} disabled={!canExecute}>
              {executing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Confirmar importación
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
